import { autoUpdate, useFloating } from '@floating-ui/react-dom';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { Button } from '../button/button';

export interface DropDownButtonOptions {
  label: string;
  value: any;
}

type Props = {
  label: string;
  options: DropDownButtonOptions[];
  onSelect: (option: DropDownButtonOptions) => void;
};

const DropdownButton = ({ label, options, onSelect }: Props) => {
  const [open, setOpen] = useState(false);

  const { refs, strategy, update, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'absolute',
  });

  React.useEffect(() => {
    if (refs.reference.current && refs.floating.current) {
      return autoUpdate(refs.reference.current, refs.floating.current, update);
    }
  }, [refs.reference, refs.floating, update]);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    if (!open) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (refs.floating.current?.contains(event.target as Node) === false) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, refs.floating, refs.reference]);

  const toggleDropdown = () => {
    setOpen(!open);
  };

  const handleOptionSelect = (option: DropDownButtonOptions) => {
    onSelect?.(option);
    setOpen(false);
  };

  return (
    <DropdownContainer>
      <Button
        color="primary"
        ref={refs.setReference}
        rightIcon={open ? { icon: 'caret-up' } : { icon: 'caret-down' }}
        variant="contained"
        onClick={toggleDropdown}
      >
        {label}
      </Button>

      {open &&
        createPortal(
          <DropdownMenu ref={refs.setFloating} style={{ ...floatingStyles, position: strategy }}>
            {options.map((option) => (
              <MenuItem key={option.value} onClick={() => handleOptionSelect(option)}>
                {option.label}
              </MenuItem>
            ))}
          </DropdownMenu>,
          document.body,
        )}
    </DropdownContainer>
  );
};

// Container that wraps the button and the dropdown list
const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

/**
 * We use an attrs function that merges the hook’s floatingStyles
 * into the dropdown’s style, ensuring correct positioning.
 */
const DropdownMenu = styled.div`
  list-style: none;
  margin: 0;
  padding: 0.5rem 0;
  border: 1px solid #ccc;
  background-color: #fff;
  z-index: 9999;
  min-width: 100px; /* adjust as needed */
`;

// The dropdown menu items (li)
const MenuItem = styled.li`
  padding: 0.5rem 1rem;
  cursor: pointer;

  &:hover {
    background-color: #eee;
  }
`;

export { DropdownButton };
